/**
 * Correction (Inventory Adjustment) Workflow Simulator
 * Simulates the complete inventory correction workflow using real API calls
 * Based on patterns from inventoryCorrectionService
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { BatchSerialHelper } from '../services/BatchSerialHelper';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

// Reason codes for corrections (matches inventoryCorrectionService)
const REASON_CODES = [
  { code: 1, description: 'Telleavvik' },
  { code: 2, description: 'Skade' },
  { code: 3, description: 'Svinn' },
  { code: 4, description: 'Systemfeil' },
  { code: 5, description: 'Manuell justering' },
  { code: 10, description: 'Annet' }
];

export interface CorrectionWorkflowResult {
  success: boolean;
  batchId?: string;
  correctionsCompleted: number;
  itemsCorrected: number;
  adjustmentsIn: number;
  adjustmentsOut: number;
  error?: string;
  duration: number;
}

export class CorrectionWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();

  constructor(config: SimulatorConfig, operator: VirtualOperator) {
    this.config = config;
    this.operator = operator;
  }

  /**
   * Execute one complete correction workflow cycle
   */
  async execute(): Promise<CorrectionWorkflowResult> {
    const startTime = Date.now();
    let batchId: string | undefined;
    let correctionsCompleted = 0;
    let itemsCorrected = 0;
    let adjustmentsIn = 0;
    let adjustmentsOut = 0;

    try {
      // Step 1: Create a new correction session
      this.operator.startTask('correction', 'Oppretter korreksjonsøkt');
      await this.rateLimiter.throttle();

      // Use operator ID + milliseconds to ensure unique BatchID across multiple simulators
      batchId = `SIM-CORR-${this.operator.getId()}-${Date.now()}`;

      await this.createCorrectionSession(batchId);

      // Step 2: Get item locations to correct
      this.operator.startTask('correction', 'Henter lokasjoner for korreksjon');
      await this.rateLimiter.throttle();

      const itemLocations = await this.getItemLocationsForCorrection();

      if (itemLocations.length === 0) {
        // Complete the session even if no items to correct
        await this.completeCorrectionSession(batchId);
        return {
          success: true,
          batchId,
          correctionsCompleted: 0,
          itemsCorrected: 0,
          adjustmentsIn: 0,
          adjustmentsOut: 0,
          duration: Date.now() - startTime,
          error: 'Ingen lokasjoner å korrigere'
        };
      }

      // Step 3: Select items to correct (max based on config)
      const maxCorrections = this.config.workflows.correction?.maxCorrections || 5;
      const itemsToCorrect = itemLocations.slice(0, Math.min(maxCorrections, itemLocations.length));

      // Step 4: Execute corrections
      const batchSerialEnabled = this.config.batchSerialConfig?.enabled ?? false;
      const batchHelper = batchSerialEnabled ? new BatchSerialHelper() : null;

      for (const item of itemsToCorrect) {
        // Randomly decide: adjustment in (transType=2) or adjustment out (transType=1)
        // For simulation, we mostly do small adjustments
        const isAdjustmentIn = Math.random() > 0.5;
        const transType = isAdjustmentIn ? 2 : 1;
        const transTypeText = isAdjustmentIn ? 'Inn på lager' : 'Ut av lager';

        // For adjustment out, limit to available quantity
        // For adjustment in, add a small random amount
        let correctionQty: number;
        if (isAdjustmentIn) {
          correctionQty = Math.floor(Math.random() * 5) + 1; // 1-5 items in
        } else {
          // Don't take more than available
          const maxOut = Math.min(item.availableQuantity || 1, 3);
          correctionQty = Math.max(1, Math.floor(Math.random() * maxOut) + 1);
        }

        // Select a random reason code
        const reasonCode = REASON_CODES[Math.floor(Math.random() * REASON_CODES.length)].code;

        this.operator.startTask(
          'correction',
          `Korrigerer ${item.itemNo} på ${item.locationName} (${transTypeText}: ${correctionQty})`
        );

        // Simulate walking to location
        await this.delay(this.config.timing.walkTimeMs);

        // Simulate counting/verification
        await this.delay(this.config.timing.countTimeMs);

        // Fetch batch/serial info if enabled
        let batchSerialFields: Record<string, string> = {};
        if (batchSerialEnabled && batchHelper) {
          const itemType = item.itemType ?? 0;
          if (isAdjustmentIn) {
            // IN: Generate new batch/serial numbers
            if (BatchSerialHelper.needsBatchTracking(itemType)) {
              batchSerialFields = {
                itemBatchNo: BatchSerialHelper.generateBatchNo(item.itemNo),
                itemBatchExpDate: BatchSerialHelper.generateExpiryDate()
              };
            } else if (BatchSerialHelper.needsSerialTracking(itemType)) {
              batchSerialFields = {
                itemSerialNo: BatchSerialHelper.generateSerialNo(item.itemNo, Date.now())
              };
            }
          } else {
            // OUT: Fetch existing batches/serials from bin
            if (BatchSerialHelper.needsBatchTracking(itemType)) {
              const batches = await batchHelper.getBatchesAtBin(item.itemNo, this.config.warehouseId, item.locationName);
              if (batches.length > 0) {
                batchSerialFields = {
                  itemBatchNo: batches[0].batchNo,
                  ...(batches[0].expiryDate ? { itemBatchExpDate: batches[0].expiryDate } : {})
                };
              }
            } else if (BatchSerialHelper.needsSerialTracking(itemType)) {
              const serials = await batchHelper.getSerialNumbersAtBin(item.itemNo, this.config.warehouseId, item.locationName);
              if (serials.length > 0) {
                batchSerialFields = { itemSerialNo: serials[0].serialNo };
              }
            }
          }
        }

        // Add correction transaction
        await this.rateLimiter.throttle();
        await this.addCorrectionTransaction(batchId, {
          itemNo: item.itemNo,
          locationName: item.locationName,
          quantity: correctionQty,
          warehouse: item.warehouseID || this.config.warehouseId,
          transType,
          reasonCode,
          description1: `Simulator korreksjon - ${REASON_CODES.find(r => r.code === reasonCode)?.description || 'Annet'}`,
          ...batchSerialFields
        });

        correctionsCompleted++;
        itemsCorrected++;
        if (isAdjustmentIn) {
          adjustmentsIn++;
        } else {
          adjustmentsOut++;
        }
      }

      // Step 5: Complete the correction session
      this.operator.startTask('correction', `Fullfører korreksjonsøkt ${batchId}`);
      await this.rateLimiter.throttle();
      await this.completeCorrectionSession(batchId);

      return {
        success: true,
        batchId,
        correctionsCompleted,
        itemsCorrected,
        adjustmentsIn,
        adjustmentsOut,
        duration: Date.now() - startTime
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[CorrectionWorkflow] Error:`, errorMessage);

      return {
        success: false,
        batchId,
        correctionsCompleted,
        itemsCorrected,
        adjustmentsIn,
        adjustmentsOut,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Create a new correction session
   */
  private async createCorrectionSession(batchId: string): Promise<any> {
    const now = new Date();

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans`,
      {
        method: 'POST',
        body: JSON.stringify({
          batchID: batchId,
          operatorID: this.operator.getId(),
          orderDate: now.toISOString(),
          description1: `Simulator korreksjon ${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')}`,
          orderStatus: 20, // Under arbeid
          orderType: 3, // Correction (matches inventoryCorrectionService)
          warehouse: this.config.warehouseId,
          startWorkDateTime: now.toISOString()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create correction session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get item locations for correction
   */
  private async getItemLocationsForCorrection(): Promise<any[]> {
    try {
      // Get all item bins and filter by warehouse
      const response = await authenticatedFetch(
        `${API_BASE_URL}/data-inspection/itembins`
      );

      if (response.ok) {
        const allBins = await response.json();
        // Filter to bins in this warehouse - for corrections we can work with any quantity
        // (including potentially negative to fix them)
        return allBins.filter((b: any) =>
          (b.warehouseID === this.config.warehouseId || b.warehouse === this.config.warehouseId)
        );
      }
    } catch (error) {
      console.warn('[CorrectionWorkflow] Could not get item locations:', error);
    }

    return [];
  }

  /**
   * Add a correction transaction
   */
  private async addCorrectionTransaction(batchId: string, data: {
    itemNo: string;
    locationName: string;
    quantity: number;
    warehouse: string;
    transType: number; // 1=ut av lager, 2=inn på lager
    reasonCode: number;
    description1?: string;
    itemBatchNo?: string;
    itemBatchExpDate?: string;
    itemSerialNo?: string;
  }): Promise<any> {
    const encodedBatchId = encodeURIComponent(batchId);

    const body: Record<string, any> = {
      batchID: batchId,
      itemNo: data.itemNo,
      locationName: data.locationName,
      quantity: data.quantity,
      warehouse: data.warehouse,
      toWarehouse: data.warehouse, // Same warehouse for correction
      operatorID: this.operator.getId(),
      lineType: 3, // Lagerkorreksjon (legacy: LineType 3)
      transType: data.transType, // 1=ut, 2=inn
      reasonCode: data.reasonCode,
      lineStatus: 20, // Under arbeid
      transDt: new Date().toISOString(),
      description1: data.description1 || '',
      quantityCorrected: data.quantity
    };

    if (data.itemBatchNo) body.itemBatchNo = data.itemBatchNo;
    if (data.itemBatchExpDate) body.itemBatchExpDate = data.itemBatchExpDate;
    if (data.itemSerialNo) body.itemSerialNo = data.itemSerialNo;

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions`,
      {
        method: 'POST',
        body: JSON.stringify(body)
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to add correction transaction: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Complete the correction session
   */
  private async completeCorrectionSession(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      // Non-critical - session may still be valid
      console.warn(`[CorrectionWorkflow] Could not complete correction session: ${response.statusText}`);
    }
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default CorrectionWorkflow;
